import { useCallback, useState } from 'react';
import type { Fairness, HistoryEntry } from '../api/types';
import { useI18n } from '../i18n';
import {
  verifyEverything,
  type FullVerification,
  type TranscriptRound,
  type VerifyResult,
} from '../fairness/transcript';

interface Props {
  matchId: string | null;
  fairness: Fairness;
  /**
   * 對局中是 `HistoryEntry[]`（從 WS 事件累積），回放是後端的 `ReplayRound[]`
   * （帶 `outcome` 正規字串）。★ 兩者都要收原文 —— 把 replay round 轉成
   * `HistoryEntry` 會**丟掉後端權威的 `outcome` 字串**，而那正是被雜湊進
   * `transcript_hash` 的東西；驗證器該用它，不該自己從 `winner` 反推。
   */
  rounds: Array<HistoryEntry | TranscriptRound>;
  /** 對局是否已結束 —— 決定要不要顯示 server_seed 那一段。 */
  ended: boolean;
  transcriptEncoding?: string;
}

/**
 * ★ 可驗證公平面板 —— 這是**教學功能**，所以它從對局一開始就在畫面上，不是藏在設定裡。
 *
 * 對局進行中：顯示 server_seed_commit（C01 §4.2 步驟 1-2）。
 * 對局結束後：顯示 server_seed + transcript_hash，並提供「在我的瀏覽器裡驗證」按鈕，
 *             用 Web Crypto 重算 SHA-256，顯示 ✅ / ❌。
 *
 * 誠實揭露：面板底部固定寫出「這不能證明什麼」（見 fairness/transcript.ts 檔頭）。
 * 把安全承諾說得比實際大，比不做還糟。
 */
export function FairnessPanel({ matchId, fairness, rounds, ended, transcriptEncoding }: Props) {
  const { t } = useI18n();
  const [result, setResult] = useState<FullVerification | null>(null);
  const [busy, setBusy] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const run = useCallback(async () => {
    if (!matchId) return;
    setBusy(true);
    try {
      const r = await verifyEverything({
        matchId,
        serverSeed: fairness.server_seed,
        serverSeedCommit: fairness.server_seed_commit,
        transcriptHash: fairness.transcript_hash ?? null,
        rounds,
        encoding: transcriptEncoding,
      });
      setResult(r);
    } finally {
      setBusy(false);
    }
  }, [matchId, fairness, rounds, transcriptEncoding]);

  return (
    <section className="fairness" aria-labelledby="fairness-title">
      <h2 className="h3" id="fairness-title">
        🔐 {t('fair.title')}
      </h2>

      <div className="fairness__row">
        <span className="faint">{t('fair.commit')}</span>
        <code className="mono">{fairness.server_seed_commit || '—'}</code>
        <span className="faint">{t('fair.commit.hint')}</span>
      </div>

      <div className="fairness__row">
        <span className="faint">{t('fair.seed')}</span>
        <code className="mono">{fairness.server_seed ?? `（${t('fair.seed.pending')}）`}</code>
      </div>

      {ended ? (
        <div className="fairness__row">
          <span className="faint">{t('fair.transcriptHash')}</span>
          <code className="mono">{fairness.transcript_hash ?? '—'}</code>
        </div>
      ) : null}

      {ended && fairness.server_seed ? (
        <div className="stack stack--tight">
          <button type="button" className="btn btn--primary btn--sm" onClick={() => void run()} disabled={busy}>
            {busy ? t('fair.verifying') : t('fair.verify')}
          </button>

          {result ? (
            <div className="stack stack--tight" aria-live="polite">
              <VerifyLine label={t('fair.commit')} result={result.commitment} />
              <VerifyLine label={t('fair.transcriptHash')} result={result.transcript} />
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowDetail((v) => !v)}>
                {showDetail ? t('common.close') : '⋯'}
              </button>
              {showDetail ? (
                <div className="stack stack--tight">
                  <span className="faint">{t('fair.computed')}</span>
                  <code className="mono">{result.transcript.computed ?? result.commitment.computed ?? '—'}</code>
                  <span className="faint">{t('fair.expected')}</span>
                  <code className="mono">{result.transcript.expected ?? result.commitment.expected ?? '—'}</code>
                  {result.transcript.preimage ? (
                    <>
                      <span className="faint">{t('fair.preimage')}</span>
                      <code className="mono">{result.transcript.preimage}</code>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <details>
        <summary className="h3">{t('fair.explain.title')}</summary>
        <p className="muted">{t('fair.explain.body')}</p>
        <p className="faint">{t('fair.explain.limit')}</p>
      </details>
    </section>
  );
}

function VerifyLine({ label, result }: { label: string; result: VerifyResult }) {
  const { t } = useI18n();
  const cls =
    result.status === 'ok' ? 'fairness__result--ok' : result.status === 'mismatch' ? 'fairness__result--bad' : 'fairness__result--unknown';
  const icon = result.status === 'ok' ? '✅' : result.status === 'mismatch' ? '❌' : '⚠️';
  const text =
    result.status === 'ok'
      ? t('fair.ok')
      : result.status === 'mismatch'
        ? t('fair.mismatch')
        : result.status === 'unsupported_encoding'
          ? t('fair.unsupported')
          : t('fair.missing');

  return (
    <p className={`fairness__result ${cls}`}>
      <span aria-hidden="true">{icon}</span>
      <span>
        {label}：{text}
      </span>
      {result.message ? <span className="faint">（{result.message}）</span> : null}
    </p>
  );
}
